/**
 * Alert Context Provider
 *
 * Provides real-time alert state management across the application.
 * Handles SSE connection, alert storage, and browser notifications.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import {
  AlertType,
  AlertSeverity,
  realtimeAlertService,
  realtimeAlertApi,
} from '../services/realtime-alert.service';
import type { Alert } from '../services/realtime-alert.service';
import {
  requestNotificationPermission,
  showBrowserNotification,
  getNotificationPermission,
  shouldShowNotification,
} from '../utils/browser-notifications';

// Context types
interface AlertContextType {
  // State
  alerts: Alert[];
  unreadCount: number;
  isConnected: boolean;
  isLoading: boolean;
  notificationPermission: NotificationPermission | 'unsupported';

  // Actions
  connect: () => void;
  disconnect: () => void;
  markAsRead: (alertId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  acknowledgeAlert: (alertId: string) => Promise<void>;
  dismissAlert: (alertId: string) => Promise<void>;
  refreshAlerts: () => Promise<void>;
  requestNotifications: () => Promise<void>;
}

// Default context value
const defaultContext: AlertContextType = {
  alerts: [],
  unreadCount: 0,
  isConnected: false,
  isLoading: false,
  notificationPermission: 'default',
  connect: () => {},
  disconnect: () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  acknowledgeAlert: async () => {},
  dismissAlert: async () => {},
  refreshAlerts: async () => {},
  requestNotifications: async () => {},
};

// Create context
const AlertContext = createContext<AlertContextType>(defaultContext);

// Provider props
interface AlertProviderProps {
  children: ReactNode;
}

// Maximum alerts to keep in memory
const MAX_ALERTS = 100;

/**
 * Alert Provider Component
 */
export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState<
    NotificationPermission | 'unsupported'
  >('default');

  // Calculate unread count
  const unreadCount = alerts.filter(a => !a.readAt && !a.dismissedAt).length;

  // Handle new alert
  const handleNewAlert = useCallback((alert: Alert) => {
    setAlerts(prev => {
      // Check if alert already exists
      if (prev.some(a => a.id === alert.id)) {
        return prev;
      }

      // Add new alert at the beginning
      const newAlerts = [alert, ...prev];

      // Limit total alerts
      if (newAlerts.length > MAX_ALERTS) {
        return newAlerts.slice(0, MAX_ALERTS);
      }

      return newAlerts;
    });

    // Show browser notification if appropriate
    if (shouldShowNotification(alert)) {
      showBrowserNotification(alert, (clickedAlert) => {
        // Navigate to alert when clicked
        window.location.href = `/alerts?id=${clickedAlert.id}`;
      });
    }
  }, []);

  // Handle connection status change
  const handleConnectionChange = useCallback((connected: boolean) => {
    setIsConnected(connected);
  }, []);

  // Connect to SSE
  const connect = useCallback(() => {
    if (!isAuthenticated) {
      console.warn('[AlertContext] Cannot connect: not authenticated');
      return;
    }

    realtimeAlertService.connect();
  }, [isAuthenticated]);

  // Disconnect from SSE
  const disconnect = useCallback(() => {
    realtimeAlertService.disconnect();
  }, []);

  // Refresh alerts from API
  const refreshAlerts = useCallback(async () => {
    if (!isAuthenticated) return;

    setIsLoading(true);
    try {
      const { alerts: fetchedAlerts } = await realtimeAlertApi.getAlerts({
        limit: MAX_ALERTS,
      });
      setAlerts(fetchedAlerts);
    } catch (error) {
      console.error('[AlertContext] Error refreshing alerts:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  // Mark alert as read
  const markAsRead = useCallback(async (alertId: string) => {
    try {
      await realtimeAlertApi.markAsRead(alertId);
      setAlerts(prev =>
        prev.map(a =>
          a.id === alertId ? { ...a, readAt: new Date().toISOString() } : a
        )
      );
    } catch (error) {
      console.error('[AlertContext] Error marking alert as read:', error);
      throw error;
    }
  }, []);

  // Mark all alerts as read
  const markAllAsRead = useCallback(async () => {
    try {
      await realtimeAlertApi.markAllAsRead();
      setAlerts(prev =>
        prev.map(a =>
          a.readAt ? a : { ...a, readAt: new Date().toISOString() }
        )
      );
    } catch (error) {
      console.error('[AlertContext] Error marking all alerts as read:', error);
      throw error;
    }
  }, []);

  // Acknowledge alert
  const acknowledgeAlert = useCallback(async (alertId: string) => {
    try {
      await realtimeAlertApi.acknowledgeAlert(alertId);
      setAlerts(prev =>
        prev.map(a =>
          a.id === alertId
            ? {
                ...a,
                acknowledgedAt: new Date().toISOString(),
                readAt: a.readAt || new Date().toISOString(),
              }
            : a
        )
      );
    } catch (error) {
      console.error('[AlertContext] Error acknowledging alert:', error);
      throw error;
    }
  }, []);

  // Dismiss alert
  const dismissAlert = useCallback(async (alertId: string) => {
    try {
      await realtimeAlertApi.dismissAlert(alertId);
      setAlerts(prev =>
        prev.map(a =>
          a.id === alertId
            ? { ...a, dismissedAt: new Date().toISOString() }
            : a
        )
      );
    } catch (error) {
      console.error('[AlertContext] Error dismissing alert:', error);
      throw error;
    }
  }, []);

  // Request notification permission
  const requestNotifications = useCallback(async () => {
    const permission = await requestNotificationPermission();
    setNotificationPermission(permission);
  }, []);

  // Set up SSE subscriptions when authenticated
  // TEMPORARILY DISABLED due to connection leak issues
  useEffect(() => {
    if (!isAuthenticated) {
      realtimeAlertService.disconnect();
      setAlerts([]);
      return undefined;
    }

    // Subscribe to alerts
    const unsubscribeAlerts = realtimeAlertService.subscribeToAlerts(handleNewAlert);
    const unsubscribeConnection = realtimeAlertService.subscribeToConnection(handleConnectionChange);

    // Connect to SSE for real-time alerts
    realtimeAlertService.disconnect();
    setTimeout(() => {
      realtimeAlertService.connect();
    }, 100);

    // Refresh alerts initially
    refreshAlerts();

    // Check notification permission
    setNotificationPermission(getNotificationPermission());

    // Cleanup
    return () => {
      unsubscribeAlerts();
      unsubscribeConnection();
      realtimeAlertService.disconnect();
    };
    // Only re-run when authentication status changes
  }, [isAuthenticated]); // Removed unstable dependencies

  // Context value
  const value: AlertContextType = {
    alerts: alerts.filter(a => !a.dismissedAt), // Filter out dismissed
    unreadCount,
    isConnected,
    isLoading,
    notificationPermission,
    connect,
    disconnect,
    markAsRead,
    markAllAsRead,
    acknowledgeAlert,
    dismissAlert,
    refreshAlerts,
    requestNotifications,
  };

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
};

/**
 * Custom hook to use alert context
 */
export const useAlerts = (): AlertContextType => {
  const context = useContext(AlertContext);

  if (context === undefined) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }

  return context;
};

/**
 * Custom hook to get alerts by severity
 */
export const useAlertsBySeverity = (severity: AlertSeverity): Alert[] => {
  const { alerts } = useAlerts();
  return alerts.filter(a => a.severity === severity);
};

/**
 * Custom hook to get alerts by type
 */
export const useAlertsByType = (type: AlertType): Alert[] => {
  const { alerts } = useAlerts();
  return alerts.filter(a => a.alertType === type);
};

/**
 * Custom hook to get alerts for a specific carrier
 */
export const useAlertsByCarrier = (dotNumber: number): Alert[] => {
  const { alerts } = useAlerts();
  return alerts.filter(a => a.dotNumber === dotNumber);
};

export default AlertContext;
